<script setup>
import { computed } from 'vue'

const props = defineProps({
    manufacturer: {
        type: Object,
        required: true
    },
    series: {
        type: Array,
        required: false,
        default: () => []
    }
})

const manufacturerSchema = computed(() => {
    // Brand schema (without @context - will be at root level)
    const brandSchema = {
        "@type": "Brand",
        "@id": `https://www.seclock.com/catalog/manufacturers/${props.manufacturer.slug}#brand`,
        "name": props.manufacturer.name || props.manufacturer.display_name,
        "url": `https://www.seclock.com/catalog/manufacturers/${props.manufacturer.slug}`,
        "description": `${props.manufacturer.name} is a manufacturer that supplies products to SECLOCK.`
    }

    // Add logo if available
    if (props.manufacturer.logo?.external_id) {
        brandSchema.logo = {
            "@type": "ImageObject",
            "url": `https://assets.seclock.com/image/upload/f_auto,q_auto/${props.manufacturer.logo.external_id}`,
            "caption": `${props.manufacturer.name || props.manufacturer.display_name} logo`,
            "width": 200,
            "height": 100
        }
    }

    // Add manufacturer website if available
    if (props.manufacturer.website) {
        brandSchema.url = props.manufacturer.website
    }

    // Organization schema (without @context - will be at root level)
    const organizationSchema = {
        "@type": "Organization",
        "@id": `https://www.seclock.com/catalog/manufacturers/${props.manufacturer.slug}#organization`,
        "name": props.manufacturer.name || props.manufacturer.display_name,
        "brand": {
            "@type": "Brand",
            "@id": `https://www.seclock.com/catalog/manufacturers/${props.manufacturer.slug}#brand`
        }
    }

    // Add logo to organization as well
    if (props.manufacturer.logo?.external_id) {
        organizationSchema.logo = brandSchema.logo
    }

    // Add product offerings from series to Organization (makesOffer is valid on Organization, not Brand)
    if (props.series && props.series.length > 0) {
        organizationSchema.makesOffer = props.series.slice(0, 10).map(serie => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "ProductGroup",
                "name": serie.name
            },
            "seller": {
                "@type": "Organization",
                "@id": "https://www.seclock.com/#organization"
            }
        }))
    }

    // Return proper JSON-LD structure with @graph for multiple schemas
    // @context is only defined once at the root level
    return {
        "@context": "https://schema.org",
        "@graph": [brandSchema, organizationSchema]
    }
})
</script>

<template>
    <component is="script" type="application/ld+json">
        {{ JSON.stringify(manufacturerSchema) }}
    </component>
</template>
